import AudiencePage, {
  type AudienceFaq,
  type AudienceItem,
  type AudienceStat,
} from "@/components/public/audience-page"
import type { SeoMeta } from "@/types"

const PAINS: AudienceItem[] = [
  {
    title: "Void periods eat the yield",
    description:
      "Every week your rental sits empty costs a week's rent, fees and (often) a chunk of your annual net yield. Social video ads consistently reduce time-to-let for BTL portfolios \u2014 a week saved is a week earned.",
  },
  {
    title: "Most tenants aren't on Rightmove alerts",
    description:
      "Gen Z and younger millennials scroll Instagram and TikTok for rentals long before they set up a portal alert. A social-first video ad is how you reach them \u2014 not a static portal listing they'll never see.",
  },
  {
    title: "Letting agents don't always market aggressively",
    description:
      "High-street letting agents vary widely in their marketing effort. If yours isn't running video ads for your rental, you're leaving yield on the table. QuiteRight gives landlords the tool to market directly.",
  },
  {
    title: "HMOs and student lets need constant marketing",
    description:
      "HMOs turn over every 6\u201312 months, student lets every academic year. Consistent video marketing across your portfolio compounds: tenants remember your brand from the last time they rented.",
  },
]

const FEATURES: AudienceItem[] = [
  { title: "Portfolio-wide branding", description: "Your landlord brand, logo and contact details on every video ad across every property. Consistent recognition for repeat tenants." },
  { title: "Works for any rental type", description: "Single-lets, HMOs, student lets, short-term \u2014 the template engine adapts to each rental type. Same pipeline, different story per property." },
  { title: "Social-first for younger tenants", description: "Instagram Reels, TikTok and Shorts in 9:16 \u2014 native formats for the demographic most likely to rent your properties." },
  { title: "Price change detection", description: "Drop the rent? QuiteRight re-renders automatically with the new price, so the social ad always matches your current position." },
  { title: "Re-let on tenancy end", description: "When a tenancy ends, kick off a fresh video for the re-let in 60 seconds. No brief, no designer, no waiting." },
  { title: "One account, multiple properties", description: "Manage your whole portfolio in one place. Each property gets its own listing and video history \u2014 so you can see what worked where." },
]

const STATS: AudienceStat[] = [
  { value: "2\u20137d", label: "faster time to let vs static listing alone", tone: "accent" },
  { value: "60s", label: "per listing, from URL to finished video ad" },
  { value: "\u00A349", label: "self-serve monthly plan for BTL landlords" },
]

const FAQ: AudienceFaq[] = [
  {
    q: "I'm a private landlord, not an agent. Does QuiteRight work for me?",
    a: "Yes \u2014 the self-serve plan at \u00A349/month is built for private landlords and small portfolio owners. Connect your Instagram and Facebook, paste a Rightmove or OpenRent URL (or upload photos directly), and QuiteRight will produce the video.",
  },
  {
    q: "Can I use my own branding rather than QuiteRight's?",
    a: "Yes. Upload your logo and pick your brand colour in the account settings \u2014 every video you render will carry your branding, not QuiteRight's. Perfect for landlords with their own letting brand or family portfolio identity.",
  },
  {
    q: "Does QuiteRight work with OpenRent and SpareRoom?",
    a: "We support URL-scraping from the major UK portals (Rightmove, Zoopla, OnTheMarket). For OpenRent and SpareRoom listings you can upload the listing photos and enter details manually \u2014 the rest of the pipeline works identically.",
  },
  {
    q: "What about HMO-specific marketing?",
    a: "HMOs work well on QuiteRight \u2014 the template handles room-by-room listings, communal spaces and per-room pricing. For student HMOs, TikTok and Instagram outperform static portal listings for filling rooms before the academic year.",
  },
  {
    q: "Is video marketing really needed for a rental?",
    a: "Not strictly needed \u2014 a portal listing alone will eventually let a property. But a few days of void can easily be worth more than a year of QuiteRight. Landlords who market aggressively on social consistently see shorter voids, better tenants, and stronger rents at renewal.",
  },
]

export default function ForBtlLandlords({ meta }: { meta: SeoMeta }) {
  return (
    <AudiencePage
      meta={meta}
      breadcrumbName="For BTL Landlords"
      heroEyebrow="For buy-to-let landlords"
      heroHeadline="Fewer void days, better tenants, higher yield"
      heroSubhead="Every week your rental sits empty costs you a week's rent. QuiteRight gives private BTL landlords the same social video marketing pipeline top agents use \u2014 so your properties let faster, to better tenants, at the rent they should."
      heroBadgeSmall="Reduce voids"
      heroBadgeLarge="with video"
      painsHeadline="Why BTL portfolios need their own video marketing"
      pains={PAINS}
      featuresEyebrow="Built for portfolio landlords"
      featuresHeadline="Your portfolio, your brand, every property"
      features={FEATURES}
      statsHeadline="BTL video marketing, in numbers"
      stats={STATS}
      faq={FAQ}
      ctaHeadline="Let faster, let better, let on your terms"
      ctaBody="Run professional social video marketing for your entire rental portfolio \u2014 directly, without relying on the agent."
      relatedKey="for/btl-landlords"
    />
  )
}
